import React from "react";
import { Checkbox, Row, Col } from "antd";

const permissionTypes = {
  labs_setup: ['add_edit', 'view', 'delete'],
  user_manager: ['add_edit', 'view', 'delete'],
  departments: ['add_edit', 'view', 'delete'],
  variance_statuses: ['add_edit', 'view', 'delete'],
  lab_statuses: ['add_edit', 'view', 'delete'],
};

const PermissionsSection = ({
  state,
  upperPermissions,
  setState,
  setUpperPermissions,
  readOnly,
}) => {
  const onChange = (checked, label, key, prevData, setHandler) => {
    // Ensure prevData is an object
    prevData = prevData || {};
    
    // If trying to check delete or export but view is not checked, prevent it
    if (!prevData?.view && checked && ["export", "delete"].includes(key)) {
      return;
    }

    // If unchecking view but add_edit is checked, prevent it
    if (key === "view" && !checked && prevData?.add_edit) {
      return;
    }

    // If checking add_edit, also check view but preserve delete state only if applicable
    if (key === "add_edit" && checked) {
      const next = {
        ...prevData,
        [key]: checked,
        view: true,
      };
      // Only carry delete if this permission group supports it
      if (Object.prototype.hasOwnProperty.call(prevData || {}, 'delete')) {
        next.delete = prevData.delete !== undefined ? prevData.delete : false;
      }
      let obj = {
        [label]: next,
      };
      return setHandler((prev) => ({
        ...prev,
        ...obj,
      }));
    }

    // If unchecking view, also uncheck export and delete (when applicable)
    if (key === "view" && !checked) {
      const updates = {
        [key]: checked,
      };
      
      // Only add export: false if the permission type has export (labs_setup)
      if (prevData.hasOwnProperty('export')) {
        updates.export = false;
      }
      // Only add delete: false if the permission type has delete
      if (prevData.hasOwnProperty('delete')) {
        updates.delete = false;
      }
      
      let obj = {
        [label]: {
          ...prevData,
          ...updates
        },
      };
      return setHandler((prev) => ({
        ...prev,
        ...obj,
      }));
    }

    // Default case for all other permission changes
    let obj = {
      [label]: {
        ...prevData,
        [key]: checked,
      },
    };
    return setHandler((prev) => ({
      ...prev,
      ...obj,
    }));
  };

  const handleLabel = (labelText) => {
    return labelText.split("_").join(" ");
  };

  const checkText = (labelText) => {
    return labelText.split("_").join(" / ");
  };

  // Create a single box for labs_setup
  const projectPermissions = ['labs_setup'];
  
  // Create a single row for all project permissions
  const upperRow = (
    <Col className="general-permissions-section upper-row">
      <Row style={{ width: '100%' }}>
        {/* LABS SETUP column */}
        <Col span={6}>
          <div className="label-div">
            <span className="uppercase-label label-div">LABS SETUP</span>
          </div>
          {Object.entries(upperPermissions?.labs_setup || {
            add_edit: false,
            view: false,
            delete: false
          }).filter(([key]) => key !== 'export').map(([key, value]) => (
            <div className="checkbox-div" key={key}>
              <Checkbox
                checked={value}
                disabled={
                  !readOnly ||
                  (!upperPermissions?.labs_setup?.view && ["delete"].includes(key))
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    'labs_setup',
                    key,
                    upperPermissions?.labs_setup,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          ))}
        </Col>
      </Row>
    </Col>
  );
  
  // Filter out the project permissions from upperPermissions for other rows
  const otherUpperRows = Object.entries(upperPermissions || {}).map((data) => {
    const [label, checkBoxData] = data;
    
    // Skip project permissions as they're handled separately
    if (projectPermissions.includes(label)) {
      return null;
    }
    
    // Ensure checkBoxData is an object with the expected properties
    const safeCheckBoxData = checkBoxData || {
      add_edit: false,
      view: false,
      export: false,
      delete: false
    };
    
    return (
      <Col key={label} className="general-permissions-section upper-row">
        <div className="label-div">
          <span className="uppercase-label ">{handleLabel(label)}</span>
        </div>
        {Object.entries(safeCheckBoxData).map((data2) => {
          const [key, value] = data2;
          return (
            <div className="checkbox-div" key={key}>
              <Checkbox
                key={key}
                checked={value}
                disabled={
                  !readOnly ||
                  (!safeCheckBoxData.view && ["export", "delete"].includes(key))
                }
                onChange={({ target }) =>
                  onChange(
                    target.checked,
                    label,
                    key,
                    safeCheckBoxData,
                    setUpperPermissions
                  )
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          );
        })}
      </Col>
    );
  }).filter(Boolean); // Filter out null values

  const jsx = Object.entries(state || {}).map((data) => {
    const [label, checkBoxData] = data;
    
    // Skip inventory_products from settings permissions
    if (label === 'inventory_products') {
      return null;
    }
    
    const safeCheckBoxData = permissionTypes[label]?.reduce((acc, perm) => ({
      ...acc,
      [perm]: checkBoxData?.[perm] || false
    }), {}) || {};
    return (
      <Col key={label} className="general-permissions-section multiple-col">
        <div className="label-div ">
          <span
            style={{
              textTransform: "capitalize",
              fontWeight: '600 !important',
              fontSize: "12px",
              lineHeight: "14px",
              whiteSpace: "nowrap",
              color: "#242424",
              fontFamily: '"poppins", Arial',
              fontStyle: "normal"
            }}
          >
            {handleLabel(label)}
          </span>
        </div>
        {Object.entries(safeCheckBoxData || {}).map((data2) => {
          const [key, value] = data2;

          return (
            <div className="checkbox-div" key={key}>
              <Checkbox
                key={key}
                disabled={!readOnly}
                checked={value}
                onChange={({ target }) =>
                  onChange(target.checked, label, key, safeCheckBoxData, setState)
                }
              >
                {checkText(key)}
              </Checkbox>
            </div>
          );
        })}
      </Col>
    );
  }).filter(Boolean);

  return (
    <>
      <Col className={`parent-div ${!readOnly ? "is-saved" : ""}`}>
        <Row gutter={[20, 30]}>
          <Col
            className="general-permission-upper-div"
            // xxl={5}
            // xl={5}
            // lg={6}
            // md={24}
          >
            <Row>{upperRow}</Row>
            {otherUpperRows.length > 0 && (
              <Row style={{ marginTop: '20px' }}>{otherUpperRows}</Row>
            )}
          </Col>
          <Col
            className="permissions  setting-permissions"
            xxl={12}
            xl={14}
            lg={14}
            md={22}
          >
            <div className="settings-div">Settings</div>
            <Row className="permission-row-div" style={{ display: 'flex', flexWrap: 'nowrap' }}>{jsx}</Row>
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default PermissionsSection;
